import React, { useEffect, useMemo, useState } from "react";
import { Toaster, toast } from "sonner";
import AdminLayout from "../components/AdminLayout";
import ClassificationEventsTable from "../components/ClassificationEventsTable";
import { api, extractError } from "../lib/api";
import {
  ChartBar,
  Users,
  BookOpenText,
  CheckSquare,
  Lightning,
  Calendar,
  TrendUp,
  Trophy,
  Buildings,
  Sparkle,
  ArrowsClockwise,
  ClockCounterClockwise,
  ShieldCheck,
  WarningCircle,
  Shield,
} from "@phosphor-icons/react";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("de-AT", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function fmtRelative(iso) {
  if (!iso) return "—";
  try {
    const sec = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (sec < 60) return "Gerade eben";
    const min = Math.floor(sec / 60);
    if (min < 60) return `Vor ${min} ${min === 1 ? "Minute" : "Minuten"}`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `Vor ${hr} ${hr === 1 ? "Stunde" : "Stunden"}`;
    const days = Math.floor(hr / 24);
    if (days < 30) return `Vor ${days} ${days === 1 ? "Tag" : "Tagen"}`;
    const months = Math.floor(days / 30);
    return `Vor ${months} ${months === 1 ? "Monat" : "Monaten"}`;
  } catch {
    return "—";
  }
}

function KpiCard({ icon: Icon, label, value, accent = "ink", subtitle, testId }) {
  const palette = {
    ink: { bar: "bg-ink-900", iconBg: "bg-ink-50 border-ink-200 text-ink-900" },
    brand: { bar: "bg-brand-400", iconBg: "bg-brand-50 border-brand-300 text-brand-700" },
    emerald: { bar: "bg-emerald-500", iconBg: "bg-emerald-50 border-emerald-200 text-emerald-700" },
    sky: { bar: "bg-sky-500", iconBg: "bg-sky-50 border-sky-200 text-sky-700" },
  }[accent] || { bar: "bg-ink-900", iconBg: "bg-ink-50 border-ink-200 text-ink-900" };
  return (
    <div className="relative border border-ink-200 bg-white rounded-sm p-5" data-testid={testId}>
      <div className={`absolute top-0 left-0 h-1 w-16 ${palette.bar}`} aria-hidden="true" />
      <div className="flex items-start gap-3">
        <div className={`h-10 w-10 shrink-0 flex items-center justify-center rounded-sm border ${palette.iconBg}`}>
          <Icon size={18} weight="duotone" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest font-semibold text-warm-500">
            {label}
          </div>
          <div className="font-cabinet text-3xl font-black tracking-tight text-ink-900 mt-1 leading-none">
            {value}
          </div>
          {subtitle && <div className="text-[11px] text-warm-500 mt-1">{subtitle}</div>}
        </div>
      </div>
    </div>
  );
}

function MiniSparkline({ series }) {
  if (!series || series.length === 0) {
    return <div className="text-xs text-warm-400 italic">Noch keine Aktivität</div>;
  }
  const max = Math.max(...series.map((d) => d.count), 1);
  const w = 240;
  const h = 36;
  const stepX = series.length === 1 ? 0 : w / (series.length - 1);
  const points = series.map((d, i) => {
    const x = i * stepX;
    const y = h - (d.count / max) * (h - 4) - 2;
    return `${x},${y}`;
  });
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} aria-hidden="true">
      <polyline
        points={points.join(" ")}
        fill="none"
        stroke="#F5A623"
        strokeWidth="1.5"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      {series.map((d, i) => {
        const x = i * stepX;
        const y = h - (d.count / max) * (h - 4) - 2;
        return <circle key={i} cx={x} cy={y} r={1.5} fill="#0F172A" />;
      })}
    </svg>
  );
}



export default function AdminStatisticsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async (silent = false) => {
    if (silent) setRefreshing(true);
    else setLoading(true);
    try {
      const { data: res } = await api.get("/admin/statistics");
      setData(res);
    } catch (err) {
      toast.error(extractError(err, "Statistik konnte nicht geladen werden."));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const totals = data?.totals || { users: 0, kvs: 0, classifications: 0 };
  const buckets = data?.time_buckets || {};
  const topKvs = data?.top_kvs || [];
  const topUsers = data?.top_users || [];
  const recents = data?.recent_events || [];
  const daily = data?.daily_series || [];

  const noActivity = totals.classifications === 0;
  const generatedAt = data?.generated_at;

  const actions = useMemo(
    () => (
      <button
        type="button"
        onClick={() => load(true)}
        disabled={refreshing}
        data-testid="admin-stats-refresh"
        className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-semibold uppercase tracking-wider rounded-sm border border-ink-300 text-ink-900 bg-white hover:bg-ink-50 transition-crisp disabled:opacity-50"
      >
        <ArrowsClockwise size={12} weight="bold" className={refreshing ? "animate-spin" : ""} />
        {refreshing ? "Wird aktualisiert…" : "Aktualisieren"}
      </button>
    ),
    [refreshing]
  );

  return (
    <AdminLayout
      title="Statistik"
      subtitle="Live-Kennzahlen über Nutzung, Einstufungen und Kollektivverträge."
      actions={actions}
    >
      <Toaster richColors closeButton position="top-right" />
      <div className="space-y-8" data-testid="admin-stats-page">
        {loading ? (
          <div className="border border-ink-200 bg-white rounded-sm p-12 text-center text-sm text-warm-600">
            Daten werden geladen …
          </div>
        ) : (
          <>
            {/* KPI grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <KpiCard
                icon={CheckSquare}
                label="Einstufungen gesamt"
                value={totals.classifications.toLocaleString("de-AT")}
                accent="brand"
                subtitle={
                  totals.classifications > 0
                    ? `Heute: ${buckets.today || 0}`
                    : "Noch keine abgeschlossenen Einstufungen"
                }
                testId="kpi-classifications"
              />
              <KpiCard
                icon={Users}
                label="Benutzer gesamt"
                value={totals.users.toLocaleString("de-AT")}
                accent="ink"
                testId="kpi-users"
              />
              <KpiCard
                icon={BookOpenText}
                label="Kollektivverträge"
                value={totals.kvs.toLocaleString("de-AT")}
                accent="emerald"
                testId="kpi-kvs"
              />
              <KpiCard
                icon={Lightning}
                label="Heute"
                value={(buckets.today || 0).toLocaleString("de-AT")}
                accent="sky"
                subtitle="Einstufungen seit 0:00 Uhr"
                testId="kpi-today"
              />
            </div>

            {/* Trend row */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div className="lg:col-span-2 border border-ink-200 bg-white rounded-sm relative" data-testid="trend-card">
                <div className="absolute top-0 left-0 h-1 w-20 bg-ink-900" aria-hidden="true" />
                <div className="px-6 py-5 border-b border-ink-200 flex items-start justify-between gap-4">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest font-semibold text-warm-500">
                      Trend
                    </div>
                    <h2 className="font-cabinet text-xl tracking-tight text-ink-900 font-medium leading-tight mt-1">
                      Einstufungs-Aktivität
                    </h2>
                  </div>
                  <div className="hidden sm:block"><MiniSparkline series={daily} /></div>
                </div>
                <div className="px-6 py-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {[
                    { label: "Heute", value: buckets.today || 0 },
                    { label: "7 Tage", value: buckets.last_7_days || 0 },
                    { label: "30 Tage", value: buckets.last_30_days || 0 },
                    { label: "365 Tage", value: buckets.last_365_days || 0 },
                  ].map((b) => (
                    <div key={b.label} className="border border-ink-200 bg-warm-50 rounded-sm p-3">
                      <div className="text-[10px] uppercase tracking-widest font-semibold text-warm-500">
                        {b.label}
                      </div>
                      <div className="font-cabinet text-2xl font-black text-ink-900 mt-0.5 leading-none">
                        {b.value.toLocaleString("de-AT")}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="border border-ink-200 bg-white rounded-sm relative" data-testid="generated-card">
                <div className="absolute top-0 left-0 h-1 w-20 bg-emerald-500" aria-hidden="true" />
                <div className="px-6 py-5 border-b border-ink-200">
                  <div className="text-[10px] uppercase tracking-widest font-semibold text-warm-500">
                    Datenstand
                  </div>
                  <h2 className="font-cabinet text-xl tracking-tight text-ink-900 font-medium leading-tight mt-1">
                    Live
                  </h2>
                </div>
                <div className="px-6 py-5 text-xs text-warm-700 leading-relaxed space-y-2">
                  <p>
                    Generiert: <strong className="text-ink-900">{fmtDate(generatedAt)}</strong>
                  </p>
                  <p>
                    Eine Einstufung gilt als <strong className="text-ink-900">abgeschlossen</strong>,
                    sobald die finale ET-Empfehlung in Schritt&nbsp;4 erstellt wurde.
                  </p>
                  <p className="text-warm-500 text-[11px]">
                    Es werden ausschließlich anonymisierte Metadaten gespeichert
                    (Benutzer-ID, KV-ID, Zeitpunkt). Hochgeladene Dokumente verlassen
                    nie den Speicher.
                  </p>
                </div>
              </div>
            </div>

            {/* No-activity hint */}
            {noActivity && (
              <div
                data-testid="stats-empty"
                className="border border-ink-200 bg-warm-50 rounded-sm p-6 text-sm text-warm-700 leading-relaxed flex items-start gap-3"
              >
                <Sparkle size={18} weight="duotone" className="text-brand-500 mt-0.5 shrink-0" />
                <div>
                  <strong className="text-ink-900">Noch keine abgeschlossenen Einstufungen.</strong>{" "}
                  Sobald Benutzer den Einstufungs-Flow bis zur finalen Empfehlung
                  durchlaufen, erscheinen hier Live-Statistiken zu Top-KVs, Top-Benutzern
                  und letzte Aktivitäten.
                </div>
              </div>
            )}

            {/* Top KVs + Top Users */}
            {!noActivity && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="border border-ink-200 bg-white rounded-sm" data-testid="top-kvs-card">
                  <div className="px-6 py-5 border-b border-ink-200 flex items-center gap-2">
                    <Trophy size={16} weight="duotone" className="text-brand-500" />
                    <h2 className="font-cabinet text-xl tracking-tight text-ink-900 font-medium leading-tight">
                      Top Kollektivverträge
                    </h2>
                  </div>
                  <ol className="divide-y divide-ink-100">
                    {topKvs.map((kv, i) => (
                      <li
                        key={kv.id}
                        data-testid={`top-kv-${i}`}
                        className="px-6 py-3.5 flex items-center gap-3"
                      >
                        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-sm bg-ink-50 border border-ink-200 text-[11px] font-bold text-ink-900">
                          {i + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-ink-900 font-medium truncate">{kv.name}</div>
                          <div className="mt-1.5 h-1 bg-ink-100 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-brand-400"
                              style={{ width: `${Math.max(kv.share * 100, 2)}%` }}
                            />
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className="font-cabinet text-base font-black text-ink-900 leading-none">
                            {kv.count}
                          </div>
                          <div className="text-[10px] text-warm-500 uppercase tracking-widest mt-0.5">
                            {(kv.share * 100).toFixed(0)}%
                          </div>
                        </div>
                      </li>
                    ))}
                  </ol>
                </div>

                <div className="border border-ink-200 bg-white rounded-sm" data-testid="top-users-card">
                  <div className="px-6 py-5 border-b border-ink-200 flex items-center gap-2">
                    <Buildings size={16} weight="duotone" className="text-ink-700" />
                    <h2 className="font-cabinet text-xl tracking-tight text-ink-900 font-medium leading-tight">
                      Top Benutzer
                    </h2>
                  </div>
                  <ol className="divide-y divide-ink-100">
                    {topUsers.map((u, i) => (
                      <li
                        key={u.id}
                        data-testid={`top-user-${i}`}
                        className="px-6 py-3.5 flex items-center gap-3"
                      >
                        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-sm bg-ink-50 border border-ink-200 text-[11px] font-bold text-ink-900">
                          {i + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-ink-900 font-medium truncate">
                            {u.name || u.email}
                          </div>
                          <div className="text-xs text-warm-500 truncate">
                            {u.company ? `${u.company} · ` : ""}
                            {u.email}
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className="font-cabinet text-base font-black text-ink-900 leading-none">
                            {u.count}
                          </div>
                          <div className="text-[10px] text-warm-500 uppercase tracking-widest mt-0.5">
                            {u.count === 1 ? "Einstufung" : "Einstufungen"}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ol>
                </div>
              </div>
            )}

            {/* Recent activity (searchable + paginated) */}
            {!noActivity && (
              <ClassificationEventsTable
                pageSize={25}
                emptyMessage="Keine Aktivitäten zu diesem Suchbegriff."
                testIdPrefix="stats-events"
              />
            )}
          </>
        )}
      </div>
    </AdminLayout>
  );
}
